<template>
    <div class="c-enquire-form c-enquire-form--small c-enquire-form--loaded">
        <transition name="fade">
            <form v-show="isMounted" class="c-enquire-form__form" @submit.prevent="submit" v-preloader="isLoading">
                <div v-show="stepId === STEP_TOUR" class="u-text-left info-top">
                    <span class="u-fa u-fa-info" slot="icon" style="margin-right: 10px; color: #ff6700;"></span>
                    {{ t('All prices shown are per person', 'enquire') }}
                </div>
                <div v-show="stepId === STEP_TOUR" class="c-enquire-form__step">
                    <div class="c-control-group" :class="{'has-error': clickedNext && showTourValidation && errors.date !== '', 'has-success': isMobileViewportSize && showTourValidation && errors.date === ''}">
                        <table-select
                            :id="`${prefix}_date`"
                            ref="date"
                            :options="dates"
                            v-model="date"
                            track-by="id"
                            label-by="display"
                            :prefix="`${t('Date', 'enquire')}:`"
                            :placeholder="t('Guaranteed departures', 'enquire')"
                            :disabled="dates.length === 0"
                            :disabled-option-callback="isDateDisabled"
                            autocomplete="random"
                            v-tooltip="tooltipOps('date')"
                            @expand="onSelectExpanded($event, 'date')">
                            <span class="u-fa u-fa-calendar" slot="icon"></span>
                        </table-select>
                        <i class="c-enquire-form__icon u-fa u-calendar-alt"></i>
                    </div>

                    <div class="c-control-group" :class="{'has-error': clickedNext && showTourValidation && errors.airport !== '', 'has-success': isMobileViewportSize && showTourValidation && errors.airport === ''}">
                        <table-select
                            :id="`${prefix}_airport`"
                            ref="airport"
                            :options="airports"
                            v-model="airport"
                            track-by="id"
                            label-by="name"
                            :prefix="`${t('Airport', 'enquire')}:`"
                            :placeholder="t('Choose Airport', 'enquire')"
                            :disabled="airports.length === 0 || innerDate === null"
                            autocomplete="random"
                            v-tooltip="tooltipOps('airport')"
                            @expand="onSelectExpanded($event, 'airport')">
                            <span class="u-fa u-fa-plane" slot="icon"></span>
                        </table-select>
                        <i class="c-enquire-form__icon u-fa u-fa-plane-departure"></i>
                    </div>

                    <div class="c-control-group" v-if="hasCombinations">
                        <table-select
                            :id="`${prefix}_combination`"
                            ref="combination"
                            :options="combinations"
                            v-model="combination"
                            track-by="id"
                            label-by="name"
                            :disabled="combinations.length === 0"
                            :prefix="`${t('Combine tour with', 'enquire')}:`"
                            :placeholder="t('Combine tour with', 'enquire')"
                            @expand="onSelectExpanded($event, 'combination')">
                        </table-select>
                    </div>

                    <div class="c-control-group">
                        <number-input :placeholder="`${t('Adults', 'enquire')}:`" :center="true" :min="1" :max="99" v-model="adults"></number-input>
                    </div>

                    <div class="c-control-group" v-if="isChildrenAllowed">
                        <number-input :placeholder="`${t('Children u/12', 'enquire')}:`" :center="true" :min="0" :max="99" v-model="children" :info-icon-text="`${t('At the time of departure', 'enquire')}`"></number-input>
                    </div>

                    <button @click="validateAndStepForward" type="button" id="form-enquire-now" class="c-form__submit c-button c-button--block c-button--large c-button--padded c-button--blue" v-text="t('Enquire Now')"></button>

                    <transition name="fade">
                        <div class="c-enquire-form__note u-text-muted" v-show="isNextStepLocked()">{{ t('Complete the form', 'enquire') }}</div>
                    </transition>
                    <div class="c-enquire-form__note" v-text="t('You will receive a free quotation.', 'enquire')"></div>
                </div>
                <div v-show="stepId === STEP_PERSONAL_INFO" class="c-enquire-form__step">
                    <a role="button" v-text="t('Back')" class="c-enquire-form__step-back" @click="stepBack"></a>
                    <div class="c-enquire-form__item">
                        <div v-tooltip="tooltipOps('name')" class="c-control-group c-control-group--inline c-control-group--combined" :class="{'has-error': clickedSubmit && showPersonalInfoValidation && errors.name !== '', 'has-success': isMobileViewportSize && showPersonalInfoValidation && errors.name === ''}">
                            <label class="c-control-label" :for="`${prefix}_name`" v-text="`${t('First name', 'enquire')}:`"></label>
                            <input name="name" ref="name" :id="`${prefix}_name`" type="text" autocomplete="given-name" class="c-input c-input--hide-autofill" v-model="name"/>
                        </div>

                        <div class="c-help-block has-error" v-if="clickedSubmit && isMobileViewportSize && showPersonalInfoValidation && errors.name !== ''" v-html="errors.name"></div>
                    </div>

                    <div class="c-enquire-form__item">
                        <div v-tooltip="tooltipOps('surname')" class="c-control-group c-control-group--inline c-control-group--combined" :class="{'has-error': clickedSubmit && showPersonalInfoValidation && errors.surname !== '', 'has-success': isMobileViewportSize && showPersonalInfoValidation && errors.surname === ''}">
                            <label class="c-control-label" :for="`${prefix}_surname`" v-text="`${t('Last Name', 'enquire')}:`"></label>
                            <input name="surname" :id="`${prefix}_surname`" type="text" class="c-input c-input--hide-autofill" autocomplete="family-name" v-model="surname"/>
                        </div>

                        <div class="c-help-block has-error" v-if="clickedSubmit && isMobileViewportSize && showPersonalInfoValidation && errors.surname !== ''" v-html="errors.surname"></div>
                    </div>

                    <div class="c-enquire-form__item">
                        <div v-tooltip="tooltipOps('email')" class="c-control-group c-control-group--inline c-control-group--combined" :class="{'has-error': clickedSubmit && showPersonalInfoValidation && errors.email !== '', 'has-success': isMobileViewportSize && showPersonalInfoValidation && errors.email === ''}">
                            <label class="c-control-label" :for="`${prefix}_email`" v-text="`${t('E-mail', 'enquire')}:`"></label>
                            <input name="email" :id="`${prefix}_email`" type="email" autocomplete="email" class="enquire-email-input c-input c-input--hide-autofill" v-model="email"/>
                        </div>

                        <div class="c-help-block has-error" v-if="clickedSubmit && isMobileViewportSize && showPersonalInfoValidation && errors.email !== ''" v-html="errors.email"></div>
                    </div>

                    <div class="c-enquire-form__item">
                        <vue-tel-input v-model="phone"
                                       :valid-characters-only="true"
                                       :id="`${prefix}_phone`"
                                       :placeholder="`${t('Phone', 'enquire')}`"
                                       :mode="'international'"
                        >
                        </vue-tel-input>
                    </div>

                    <div class="c-enquire-form__item" v-if="hasGender">
                        <div v-tooltip="tooltipOps('gender')" class="c-control-group" :class="{'has-error': clickedSubmit && showPersonalInfoValidation && errors.gender !== '', 'has-success': isMobileViewportSize && showPersonalInfoValidation && errors.gender === ''}">
                            <table-select
                                :id="`${prefix}_gender`"
                                ref="gender"
                                :options="genders"
                                v-model="gender"
                                track-by="value"
                                label-by="display"
                                :prefix="`${t('Title')}:`"
                                :placeholder="t('Title')"
                                @expand="onSelectExpanded($event, 'gender')">
                            </table-select>

                            <div class="c-help-block has-error" v-if="clickedSubmit && isMobileViewportSize && showPersonalInfoValidation && errors.gender !== ''" v-html="errors.gender"></div>
                        </div>
                    </div>

                    <div class="c-control-group" v-tooltip="tooltipOps('marketingSource')" v-if="marketingSources && marketingSources.length" :class="{'has-error': clickedSubmit && showPersonalInfoValidation && errors.marketingSource !== '', 'has-success': isMobileViewportSize && showPersonalInfoValidation && errors.marketingSource === ''}">
                        <table-select
                            :id="`${prefix}_marketingSource`"
                            ref="marketingSource"
                            :options="marketingSources"
                            v-model="marketingSource"
                            track-by="id"
                            label-by="name"
                            :placeholder="t('How did you hear about us', 'enquire')"
                            @expand="onSelectExpanded($event, 'marketingSource')"
                        ></table-select>
                    </div>

                    <div v-if="isMarketingSourceIdOther" class="c-enquire-form__item">
                        <div v-tooltip="tooltipOps('otherDescription')" class="c-control-group c-control-group--inline c-control-group--combined c-control-group--combined-multiline u-mb-3"
                             :class="{'has-error': clickedSubmit && showPersonalInfoValidation && errors.otherDescription === '', 'has-success': isMobileViewportSize && showPersonalInfoValidation && errors.otherDescription !== ''}">
                            <label class="c-control-label" :for="`${prefix}_otherDescription`" v-text="`${t('How did you hear about us', 'enquire')}:`"></label>
                            <textarea :id="`${prefix}_otherDescription`" name="otherDescription" rows="2" class="c-textarea c-textarea--hide-autofill" v-model="otherDescription"></textarea>
                        </div>
                    </div>

                    <div class="c-enquire-form__item">
                        <div class="c-control-group c-control-group--inline c-control-group--combined c-control-group--combined-multiline u-mb-3">
                            <label class="c-control-label" :for="`${prefix}_comment`" v-text="`${t('Comment', 'enquire')}:`"></label>
                            <textarea :id="`${prefix}_comment`" name="comment" rows="3" class="c-textarea c-textarea--hide-autofill" v-model="comment"></textarea>
                        </div>
                    </div>

                    <!--                    <div class="c-enquire-form__item" v-show="hasNewsletter">
                                            <div class="c-control-group">
                                                <div class="c-checkbox c-checkbox&#45;&#45;skin-white">
                                                    <label class="c-checkbox__label">
                                                        <input name="newsletter" type="checkbox" class="c-checkbox__input" v-model="newsletter" />
                                                        <span class="c-checkbox__icon"></span>
                                                        <span class="c-checkbox__text" v-text="t('Receive our newsletter.', 'enquire')"></span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>-->

                    <button @click="clickedSubmit = true" type="submit" id="form-request-free-quotation" class="c-form__submit c-enquire-form__submit c-button c-button--block c-button--large c-button--padded c-button--blue" v-text="t('Request free quotation', 'enquire')"></button>

                    <div class="c-enquire-form__note c-enquire-form__note--absolute" v-text="t('You will receive a free quotation.', 'enquire')"></div>
                </div>
                <div v-if="stepId === STEP_CAPTCHA" class="c-enquire-form__step">
                    <captcha :sitekey="captchaSiteKey" @response="onCaptchaResponse"></captcha>
                </div>
            </form>
        </transition>
    </div>
</template>
<script lang="ts">
import { IDepartureDate } from '@/store/models';
import moment from 'moment';
import { Component, Prop } from 'vue-property-decorator';
import EnquireForm from './EnquireForm.vue';

@Component
export default class EnquireFormSmall extends EnquireForm {
    @Prop({ type: String, default: 'Tour Page Top Enquire Form' }) protected readonly gtmCategory: string;

    declare protected STEP_PERSONAL_INFO: number;
    declare protected airportId: number | null;
    declare protected combinationId: number | null;
    declare protected defaultFocusedInput: string | null;
    declare protected errors: { [key: string]: string };
    declare protected readonly dates: IDepartureDate[];
    declare protected innerDate: number | string | null;
    declare protected isMobileViewportSize: boolean;
    declare protected showTourValidation: boolean;
    declare protected stepId: number;

    protected clickedNext: boolean = false;

    protected clickedSubmit: boolean = false;

    public tooltipOps(field: string): any {
        const isTourValidation = ['destination', 'tour', 'date', 'airport', 'adults', 'children'].indexOf(field) > -1;
        let isTypeCorrepondToPage = false;
        if (this.clickedNext && isTourValidation && this.stepId !== this.STEP_PERSONAL_INFO) {
            isTypeCorrepondToPage = true;
        } else if (this.clickedSubmit && !isTourValidation && this.stepId === this.STEP_PERSONAL_INFO) {
            isTypeCorrepondToPage = true;
        }

        return {
            enabled: !this.isMobileViewportSize,
            content: this.errors[field],
            classes: 'c-tooltip--error',
            show: isTypeCorrepondToPage && this.errors[field] !== '',
            trigger: 'manual',
            autoHide: false,
            hideOnTargetClick: false,
            placement: 'left',
            offset: 5,
        };
    }

    protected onExternalDateSet(date: number | null): void {
        if (date === null) {
            this.innerDate = null;
            this.defaultFocusedInput = 'date';
        } else {
            const value = moment(date).format('YYYY-MM-DD');
            const targetDate: IDepartureDate | null = this.dates.find((d) => d.value === value) || null;
            this.defaultFocusedInput = null;
            this.innerDate = targetDate !== null ? targetDate.id : null;
        }
        this.combinationId = 0;
        this.airportId = null;
    }

    protected validateAndStepForward(): void {
        if (!this.clickedNext) {
            this.clickedNext = true;
        }
        if (this.errors.destination !== '' || this.errors.tour !== '' || this.errors.date !== ''
            || this.errors.airport !== '') {
            this.showTourValidation = true;
        } else {
            this.stepId = this.STEP_PERSONAL_INFO;
        }
    }
}
</script>
